import { useState, useEffect, useRef, useCallback } from 'react';
import '../assets/css/--component-chat-widget.css';
import {
    VOICE_SAMPLE_RATE,
    VOICE_BUFFER_SIZE,
    USER_AUDIO_FORMAT,
    decodeAgentAudio,
    downsampleBuffer,
    encodeUserAudio,
} from '../utils/voiceAudioCodec';

// ============================================
// CONSTANTS
// ============================================
const WS_READY_STATE = {
    CONNECTING: 0,
    OPEN: 1,
    CLOSING: 2,
    CLOSED: 3,
};

const MIN_RECORDING_DURATION = 1; // seconds
const RECORDING_TIMESLICE = 100; // ms - MediaRecorder timeslice
const WS_SEND_DELAY = 500; // ms - delay before sending chat_start
const HAPTIC_START = 50; // ms
const HAPTIC_STOP = 100; // ms
const AMPLITUDE_BARS_COUNT = 20;
const AMPLITUDE_MAX_HEIGHT = 22;
const AMPLITUDE_MIN_HEIGHT = 3;
const AMPLITUDE_SCALE = 60;
const BARGE_IN_RMS_THRESHOLD = 0.075;
const BARGE_IN_CONSECUTIVE_FRAMES = 4;
const BARGE_IN_COOLDOWN_MS = 1200;
const BARGE_IN_PLAYBACK_GRACE_MS = 450;

// ============================================
// UTILITIES
// ============================================

/**
 * Converts an audio Blob to a Base64-encoded string
 * @param {Blob} blob - Audio blob to convert
 * @returns {Promise<string>} Base64 encoded string
 */
const blobToBase64 = async (blob) => {
    const arrayBuffer = await blob.arrayBuffer();
    return btoa(
        new Uint8Array(arrayBuffer)
            .reduce((data, byte) => data + String.fromCharCode(byte), '')
    );
};

/**
 * Formats seconds into a readable duration string
 * @param {number} seconds
 * @returns {string} Formatted duration (e.g., "1:05" or "45s")
 */
const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0
        ? `${mins}:${secs.toString().padStart(2, '0')}`
        : `${secs}s`;
};

/**
 * Generates a unique session ID
 * @returns {string} Unique session identifier
 */
const generateSessionId = () =>
    `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Triggers haptic feedback if supported
 * @param {number} duration - Vibration duration in ms
 */
const triggerHaptic = (duration) => {
    if (navigator.vibrate) {
        navigator.vibrate(duration);
    }
};

const normalizeTranscript = (text) =>
    (text || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const stripEchoedBotText = (transcript, botText) => {
    const normalizedTranscript = normalizeTranscript(transcript);
    const normalizedBotText = normalizeTranscript(botText);

    if (!normalizedTranscript || !normalizedBotText || normalizedBotText.length < 24) {
        return transcript;
    }

    if (normalizedTranscript === normalizedBotText) {
        return '';
    }

    if (normalizedTranscript.startsWith(normalizedBotText)) {
        return transcript.slice(botText.length).trim();
    }

    if (normalizedBotText.includes(normalizedTranscript) && normalizedTranscript.length > 24) {
        return '';
    }

    return transcript;
};

const getRmsLevel = (samples) => {
    if (!samples?.length) {
        return 0;
    }

    let sum = 0;
    for (let i = 0; i < samples.length; i++) {
        sum += samples[i] * samples[i];
    }

    return Math.sqrt(sum / samples.length);
};

// ============================================
// COMPONENT
// ============================================
export default function ChatComponent({
    agentId,
    userId,
    wsUrl = `${process.env.REACT_APP_SOCKET_URL}/chat`,
    backgroundColor = '',
    textColor = '',
    headerColor = '',
    buttonColor = '',
    borderRadius = '',
    chatTextColor = '',
}) {
    // -----------------------------------------
    // STATE
    // -----------------------------------------
    const [messages, setMessages] = useState([]);
    const [typing, setTyping] = useState(false);
    const [input, setInput] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [isVoiceChatActive, setIsVoiceChatActive] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [isMinimized, setIsMinimized] = useState(true);
    const [connectionError, setConnectionError] = useState(null);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const [recordingAmplitude, setRecordingAmplitude] = useState(0);

    // -----------------------------------------
    // STORAGE KEY FOR MESSAGE PERSISTENCE
    // -----------------------------------------
    const STORAGE_KEY = `chat_messages_${agentId}_${userId}`;

    // -----------------------------------------
    // REFS
    // -----------------------------------------
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const wsRef = useRef(null);
    const sessionId = useRef(generateSessionId());

    // Recording refs
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const recordingTimerRef = useRef(null);
    const audioContextRef = useRef(null);
    const playbackAudioContextRef = useRef(null);
    const playbackTimeRef = useRef(0);
    const playbackSourcesRef = useRef(new Set());
    const agentSpeakingUntilRef = useRef(0);
    const agentSpeakingStartedAtRef = useRef(0);
    const bargeInVoiceFramesRef = useRef(0);
    const lastBargeInAtRef = useRef(0);
    const ignoreAgentAudioUntilRef = useRef(0);
    const lastBotMessageRef = useRef('');
    const analyserRef = useRef(null);
    const animationFrameRef = useRef(null);
    const streamRef = useRef(null);
    const mediaStreamSourceRef = useRef(null);
    const processorRef = useRef(null);
    const isRecordingRef = useRef(false);
    const isVoiceChatActiveRef = useRef(false);
    const voiceSessionReadyRef = useRef(false);
    const voiceChunkCountRef = useRef(0);
    const activeTranscriptMessageIdRef = useRef(null);
    const recordingStartTimeRef = useRef(null);
    const inputModeRef = useRef('text');

    // -----------------------------------------
    // LOAD MESSAGES FROM LOCAL STORAGE
    // -----------------------------------------
    useEffect(() => {
        const savedMessages = localStorage.getItem(STORAGE_KEY);
        if (savedMessages) {
            try {
                const parsedMessages = JSON.parse(savedMessages);
                setMessages(parsedMessages);
                console.log('Loaded saved messages from localStorage:', parsedMessages.length);
            } catch (error) {
                console.error('Error loading messages from localStorage:', error);
            }
        }
    }, [STORAGE_KEY]);

    // -----------------------------------------
    // SAVE MESSAGES TO LOCAL STORAGE
    // -----------------------------------------
    useEffect(() => {
        if (messages.length > 0) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
            console.log('Saved messages to localStorage:', messages.length);
        }
    }, [messages, STORAGE_KEY]);

    // -----------------------------------------
    // AUTO SCROLL
    // -----------------------------------------
    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, typing, scrollToBottom]);

    // -----------------------------------------
    // MESSAGE HANDLER
    // -----------------------------------------
    const appendMessageToUI = useCallback((sender, text) => {
        if (!text) return; // Guard against empty messages

        console.log(`[Chat] ${sender}: ${text}`);
        setMessages((prev) => [
            ...prev,
            {
                sender,
                text,
                timestamp: new Date().toLocaleTimeString(),
            },
        ]);
    }, []);

    const upsertVoiceTranscript = useCallback((text, isFinal = false) => {
        const trimmed = text?.trim();
        if (!trimmed) return;

        setMessages((prev) => {
            const timestamp = new Date().toLocaleTimeString();
            const activeId = activeTranscriptMessageIdRef.current;

            if (activeId) {
                const existingIndex = prev.findIndex((msg) => msg.id === activeId);
                if (existingIndex !== -1) {
                    const updated = [...prev];
                    updated[existingIndex] = {
                        ...updated[existingIndex],
                        text: trimmed,
                        timestamp,
                    };

                    if (isFinal) {
                        activeTranscriptMessageIdRef.current = null;
                    }

                    return updated;
                }
            }

            const id = `voice-transcript-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            activeTranscriptMessageIdRef.current = isFinal ? null : id;

            return [
                ...prev,
                {
                    id,
                    sender: 'user',
                    text: trimmed,
                    timestamp,
                },
            ];
        });
    }, []);

    // -----------------------------------------
    // WEBSOCKET HELPERS
    // -----------------------------------------
    const isWebSocketOpen = useCallback(() => {
        return wsRef.current?.readyState === WS_READY_STATE.OPEN;
    }, []);

    const safeSend = useCallback((data) => {
        if (!isWebSocketOpen()) {
            console.warn('[WS] Cannot send — WebSocket not open');
            return false;
        }

        try {
            wsRef.current.send(JSON.stringify(data));
            return true;
        } catch (error) {
            console.error('[WS] Send error:', error);
            return false;
        }
    }, [isWebSocketOpen]);

    // -----------------------------------------
    // SEND TEXT MESSAGE
    // -----------------------------------------
    const sendMessage = useCallback((messageText) => {
        const trimmed = messageText?.trim();
        if (!trimmed || !isWebSocketOpen()) {
            console.log('[Chat] Cannot send message:', {
                hasText: !!trimmed,
                wsOpen: isWebSocketOpen(),
            });
            return;
        }

            appendMessageToUI('user', trimmed);

            safeSend({
                event: 'chat_message',
                sessionId: sessionId.current,
                inputMode: 'text',
                message: trimmed,
            });

        setInput('');
    }, [isWebSocketOpen, appendMessageToUI, safeSend]);

    // -----------------------------------------
    // VOICE MESSAGE
    // -----------------------------------------
    const sendVoiceMessageLegacy = useCallback(async (blob, duration) => {
        if (!isWebSocketOpen()) {
            console.error('[Voice] WebSocket not connected');
            appendMessageToUI('system', 'Not connected to server');
            return;
        }

        if (duration < MIN_RECORDING_DURATION) {
            console.log('[Voice] Recording too short:', duration);
            appendMessageToUI('system', 'Recording too short. Please try again.');
            return;
        }

        try {
            const base64Audio = await blobToBase64(blob);

            appendMessageToUI('user', `🎤 Voice message (${formatDuration(duration)})`);

            safeSend({
                event: 'voice_message',
                sessionId: sessionId.current,
                audio: base64Audio,
                duration,
            });

            console.log('[Voice] Sent successfully, duration:', duration);
        } catch (error) {
            console.error('[Voice] Error sending:', error);
            appendMessageToUI('system', 'Failed to send voice message');
        }
    }, [isWebSocketOpen, appendMessageToUI, safeSend]);

    const safeSendBinary = useCallback((buffer) => {
        if (!isWebSocketOpen()) {
            return false;
        }

        try {
            wsRef.current.send(buffer);
            return true;
        } catch (error) {
            console.error('[Voice] Binary send error:', error);
            return false;
        }
    }, [isWebSocketOpen]);

    const stopAgentPlayback = useCallback(async () => {
        playbackSourcesRef.current.forEach((source) => {
            try {
                source.stop();
            } catch (error) {
                // Ignore sources that have already ended.
            }
        });
        playbackSourcesRef.current.clear();
        agentSpeakingUntilRef.current = 0;
        agentSpeakingStartedAtRef.current = 0;
        bargeInVoiceFramesRef.current = 0;
        playbackTimeRef.current = 0;

        if (playbackAudioContextRef.current) {
            try {
                await playbackAudioContextRef.current.close();
            } catch (error) {
                console.warn('[Voice] Playback context close error:', error);
            }
            playbackAudioContextRef.current = null;
        }
    }, []);

    const playAgentAudio = useCallback((base64Audio) => {
        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!playbackAudioContextRef.current) {
                playbackAudioContextRef.current = new AudioContextClass({ sampleRate: VOICE_SAMPLE_RATE });
                playbackTimeRef.current = playbackAudioContextRef.current.currentTime;
            }

            const audioContext = playbackAudioContextRef.current;
            const samples = decodeAgentAudio(base64Audio);
            const audioBuffer = audioContext.createBuffer(1, samples.length, VOICE_SAMPLE_RATE);
            audioBuffer.copyToChannel(samples, 0);

            const source = audioContext.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(audioContext.destination);
            source.onended = () => {
                playbackSourcesRef.current.delete(source);
            };

            const startAt = Math.max(audioContext.currentTime, playbackTimeRef.current);
            const wasAgentSpeaking = audioContext.currentTime < agentSpeakingUntilRef.current;
            playbackSourcesRef.current.add(source);
            source.start(startAt);
            if (!wasAgentSpeaking) {
                agentSpeakingStartedAtRef.current = audioContext.currentTime;
            }
            playbackTimeRef.current = startAt + audioBuffer.duration;
            agentSpeakingUntilRef.current = playbackTimeRef.current + 0.35;
        } catch (error) {
            console.error('[Voice] Playback error:', error);
            appendMessageToUI('system', 'Unable to play agent audio');
        }
    }, [appendMessageToUI]);

    // -----------------------------------------
    // RECORDING AMPLITUDE VISUALIZATION
    // -----------------------------------------
    const startAmplitudeVisualization = useCallback(() => {
        if (!audioContextRef.current || !analyserRef.current) return;

        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

        const updateAmplitude = () => {
            if (!isRecordingRef.current || !analyserRef.current) return;

            analyserRef.current.getByteTimeDomainData(dataArray);

            let maxSample = 0;
            for (let i = 0; i < dataArray.length; i++) {
                const normalized = (dataArray[i] - 128) / 128;
                maxSample = Math.max(maxSample, Math.abs(normalized));
            }

            setRecordingAmplitude(maxSample);
            animationFrameRef.current = requestAnimationFrame(updateAmplitude);
        };

        updateAmplitude();
    }, []);

    // -----------------------------------------
    // RECORDING TIMER EFFECT
    // -----------------------------------------
    useEffect(() => {
        if (isRecording) {
            // Start timer
            recordingTimerRef.current = setInterval(() => {
                if (recordingStartTimeRef.current) {
                    const elapsed = Math.floor(
                        (Date.now() - recordingStartTimeRef.current) / 1000
                    );
                    setRecordingDuration(elapsed);
                }
            }, RECORDING_TIMESLICE);

            startAmplitudeVisualization();
        } else {
            // Cleanup timer and animation
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
                recordingTimerRef.current = null;
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            setRecordingDuration(0);
            setRecordingAmplitude(0);
        }

        return () => {
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
                recordingTimerRef.current = null;
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, [isRecording, startAmplitudeVisualization]);

    // -----------------------------------------
    // RECORDING CLEANUP HELPER
    // -----------------------------------------
    const cleanupRecordingResources = useCallback(async () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }

        if (audioContextRef.current) {
            try {
                await audioContextRef.current.close();
            } catch (err) {
                console.warn('[Audio] Context close error:', err);
            }
            audioContextRef.current = null;
        }

        analyserRef.current = null;
        audioChunksRef.current = [];
        isRecordingRef.current = false;
        recordingStartTimeRef.current = null;
    }, []);

    // -----------------------------------------
    // START RECORDING
    // -----------------------------------------
    const startRecording = useCallback(async () => {
        console.log('[Recording] Start requested', {
            isConnected,
            isRecording: isRecordingRef.current,
        });

        if (!isConnected) {
            setConnectionError('Not connected to server');
            return;
        }

        if (isRecordingRef.current) {
            console.log('[Recording] Already recording');
            return;
        }

        try {
            recordingStartTimeRef.current = Date.now();

            // Request microphone
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            // Setup audio context for visualization
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!audioContextRef.current) {
                audioContextRef.current = new AudioContextClass();
            }
            analyserRef.current = audioContextRef.current.createAnalyser();
            const source = audioContextRef.current.createMediaStreamSource(stream);
            source.connect(analyserRef.current);

            // Setup MediaRecorder
            const recorder = new MediaRecorder(stream);
            mediaRecorderRef.current = recorder;
            audioChunksRef.current = [];

            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            recorder.onstop = async () => {
                const finalDuration = Math.floor(
                    (Date.now() - (recordingStartTimeRef.current || Date.now())) / 1000
                );
                console.log('[Recording] Stopped, chunks:', audioChunksRef.current.length, 'duration:', finalDuration);

                if (audioChunksRef.current.length > 0 && isRecordingRef.current) {
                    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                    await sendVoiceMessageLegacy(audioBlob, finalDuration);
                }

                await cleanupRecordingResources();
            };

            // Start
            recorder.start(RECORDING_TIMESLICE);
            setIsRecording(true);
            isRecordingRef.current = true;
            console.log('[Recording] Started');

            triggerHaptic(HAPTIC_START);
        } catch (error) {
            console.error('[Recording] Microphone error:', error);
            setConnectionError('Microphone access denied. Please check permissions.');
            await cleanupRecordingResources();
        }
    }, [isConnected, sendVoiceMessageLegacy, cleanupRecordingResources]);

    // -----------------------------------------
    // STOP RECORDING
    // -----------------------------------------
    const stopRecording = useCallback(() => {
        console.log('[Recording] Stop requested', {
            isRecording: isRecordingRef.current,
        });

        if (mediaRecorderRef.current && isRecordingRef.current) {
            try {
                mediaRecorderRef.current.stop();
            } catch (error) {
                console.error('[Recording] Stop error:', error);
            }
            setIsRecording(false);
            console.log('[Recording] Stopped, processing...');
            triggerHaptic(HAPTIC_STOP);
        }
    }, []);

    // -----------------------------------------
    // TOGGLE RECORDING
    // -----------------------------------------
    const toggleRecording = useCallback(() => {
        if (isRecordingRef.current) {
            stopRecording();
        } else {
            startRecording();
        }
    }, [startRecording, stopRecording]);

    const cleanupVoiceResources = useCallback(async () => {
        if (processorRef.current) {
            try {
                processorRef.current.disconnect();
            } catch (err) {
                console.warn('[Audio] Processor disconnect error:', err);
            }
            processorRef.current.onaudioprocess = null;
            processorRef.current = null;
        }

        if (mediaStreamSourceRef.current) {
            try {
                mediaStreamSourceRef.current.disconnect();
            } catch (err) {
                console.warn('[Audio] Source disconnect error:', err);
            }
            mediaStreamSourceRef.current = null;
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }

        if (audioContextRef.current) {
            try {
                await audioContextRef.current.close();
            } catch (err) {
                console.warn('[Audio] Context close error:', err);
            }
            audioContextRef.current = null;
        }

        analyserRef.current = null;
        isRecordingRef.current = false;
        isVoiceChatActiveRef.current = false;
        voiceSessionReadyRef.current = false;
        voiceChunkCountRef.current = 0;
        agentSpeakingStartedAtRef.current = 0;
        bargeInVoiceFramesRef.current = 0;
        lastBargeInAtRef.current = 0;
        ignoreAgentAudioUntilRef.current = 0;
        activeTranscriptMessageIdRef.current = null;
        recordingStartTimeRef.current = null;
        setIsRecording(false);
        setIsVoiceChatActive(false);
    }, []);

    const startVoiceChat = useCallback(async () => {
        if (!isConnected) {
            setConnectionError('Not connected to server');
            return;
        }

        if (isVoiceChatActiveRef.current) {
            return;
        }

        try {
            recordingStartTimeRef.current = Date.now();
            voiceSessionReadyRef.current = false;
            inputModeRef.current = 'voice';
            safeSend({ event: 'chat_end' });
            sessionId.current = generateSessionId();

            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            streamRef.current = stream;

            safeSend({
                event: 'chat_start',
                agentId,
                inputMode: 'voice',
                sessionId: sessionId.current,
            });

            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            audioContextRef.current = new AudioContextClass();
            if (audioContextRef.current.state === 'suspended') {
                await audioContextRef.current.resume();
            }
            analyserRef.current = audioContextRef.current.createAnalyser();
            analyserRef.current.fftSize = 2048;

            const source = audioContextRef.current.createMediaStreamSource(stream);
            const processor = audioContextRef.current.createScriptProcessor(VOICE_BUFFER_SIZE, 1, 1);
            mediaStreamSourceRef.current = source;
            processorRef.current = processor;

            processor.onaudioprocess = (event) => {
                if (!isVoiceChatActiveRef.current || !voiceSessionReadyRef.current || !isWebSocketOpen()) {
                    return;
                }

                const inputData = event.inputBuffer.getChannelData(0);
                const isAgentSpeaking = playbackAudioContextRef.current &&
                    playbackAudioContextRef.current.currentTime < agentSpeakingUntilRef.current;

                if (isAgentSpeaking) {
                    if (
                        agentSpeakingStartedAtRef.current &&
                        playbackAudioContextRef.current.currentTime - agentSpeakingStartedAtRef.current <
                        BARGE_IN_PLAYBACK_GRACE_MS / 1000
                    ) {
                        return;
                    }

                    const rmsLevel = getRmsLevel(inputData);
                    const now = Date.now();
                    const canBargeIn = now - lastBargeInAtRef.current > BARGE_IN_COOLDOWN_MS;

                    if (rmsLevel >= BARGE_IN_RMS_THRESHOLD && canBargeIn) {
                        bargeInVoiceFramesRef.current += 1;
                    } else {
                        bargeInVoiceFramesRef.current = 0;
                    }

                    if (bargeInVoiceFramesRef.current < BARGE_IN_CONSECUTIVE_FRAMES) {
                        return;
                    }

                    lastBargeInAtRef.current = now;
                    ignoreAgentAudioUntilRef.current = now + 1200;
                    bargeInVoiceFramesRef.current = 0;
                    console.log('[Voice] Barge-in detected, stopping agent playback');
                    stopAgentPlayback();
                }

                const downsampled = downsampleBuffer(
                    inputData,
                    audioContextRef.current.sampleRate,
                    VOICE_SAMPLE_RATE
                );
                safeSendBinary(encodeUserAudio(downsampled));
                voiceChunkCountRef.current += 1;

                if (voiceChunkCountRef.current === 1 || voiceChunkCountRef.current % 50 === 0) {
                    console.log('[Voice] Sent audio chunks:', voiceChunkCountRef.current, {
                        format: USER_AUDIO_FORMAT,
                        sampleRate: VOICE_SAMPLE_RATE,
                        samples: downsampled.length,
                    });
                }
            };

            source.connect(analyserRef.current);
            analyserRef.current.connect(processor);
            processor.connect(audioContextRef.current.destination);

            isRecordingRef.current = true;
            isVoiceChatActiveRef.current = true;
            setIsRecording(true);
            setIsVoiceChatActive(true);
            appendMessageToUI('system', 'Voice chat started');
            triggerHaptic(HAPTIC_START);
        } catch (error) {
            console.error('[Voice] Microphone error:', error);
            setConnectionError('Microphone access denied. Please check permissions.');
            await cleanupVoiceResources();
        }
    }, [agentId, appendMessageToUI, cleanupVoiceResources, isConnected, isWebSocketOpen, safeSend, safeSendBinary, stopAgentPlayback]);

    const stopVoiceChat = useCallback(async () => {
        if (!isVoiceChatActiveRef.current) {
            return;
        }

        safeSend({ event: 'chat_end' });
        await stopAgentPlayback();
        await cleanupVoiceResources();
        inputModeRef.current = 'text';
        sessionId.current = generateSessionId();
        safeSend({
            event: 'chat_start',
            agentId,
            inputMode: 'text',
            sessionId: sessionId.current,
        });
        appendMessageToUI('system', 'Voice chat ended');
        triggerHaptic(HAPTIC_STOP);
    }, [agentId, appendMessageToUI, cleanupVoiceResources, safeSend, stopAgentPlayback]);

    const toggleVoiceChat = useCallback(() => {
        if (isVoiceChatActiveRef.current) {
            stopVoiceChat();
        } else {
            startVoiceChat();
        }
    }, [startVoiceChat, stopVoiceChat]);

    // -----------------------------------------
    // CHAT START EVENT
    // -----------------------------------------
    const sendChatStartEvent = useCallback(() => {
        inputModeRef.current = 'text';
        safeSend({
            event: 'chat_start',
            agentId,
            inputMode: 'text',
            sessionId: sessionId.current,
        });
        console.log('[WS] Sent chat_start event');
    }, [agentId, safeSend]);

    // -----------------------------------------
    // WEBSOCKET MESSAGE HANDLER
    // -----------------------------------------
    const handleWebSocketMessage = useCallback((event) => {
        try {
            const data = JSON.parse(event.data);
            console.log('[WS] Received:', data);

            const eventType = data.event || data.type;

            switch (eventType) {
                case 'chat_started':
                    if (data.sessionId) {
                        sessionId.current = data.sessionId;
                    }
                    if (data.inputMode) {
                        inputModeRef.current = data.inputMode;
                    }
                    voiceSessionReadyRef.current = (data.inputMode || inputModeRef.current) === 'voice';
                    appendMessageToUI('system', 'Session started');
                    break;

                case 'agent_message':
                    activeTranscriptMessageIdRef.current = null;
                    lastBotMessageRef.current = data.text || '';
                    appendMessageToUI('bot', data.text);
                    setTyping(false);
                    break;

                case 'agent_audio':
                    if (data.audio && isVoiceChatActiveRef.current && Date.now() >= ignoreAgentAudioUntilRef.current) {
                        playAgentAudio(data.audio);
                    }
                    break;

                case 'user_transcript':
                    upsertVoiceTranscript(
                        stripEchoedBotText(data.text, lastBotMessageRef.current),
                        data.isFinal || data.final || data.final_transcript
                    );
                    break;

                case 'interruption':
                    stopAgentPlayback();
                    setTyping(false);
                    break;

                case 'session_ended':
                    voiceSessionReadyRef.current = false;
                    stopAgentPlayback();
                    appendMessageToUI('system', 'Session ended');
                    setTyping(false);
                    break;

                case 'typing':
                    setTyping(data.isTyping);
                    break;

                case 'error':
                    console.error('[WS] Server error:', data.message);
                    appendMessageToUI('system', `Error: ${data.message}`);
                    break;

                default:
                    // Handle legacy/fallback message formats
                    if (data.sender === 'bot') {
                        appendMessageToUI('bot', data.text || data.message);
                    } else if (data.sender === 'user') {
                        appendMessageToUI('user', data.text || data.message);
                    } else if (data.message) {
                        appendMessageToUI('system', data.message);
                    }
                    break;
            }
        } catch (error) {
            console.error('[WS] Parse error:', error);
        }
    }, [appendMessageToUI, playAgentAudio, stopAgentPlayback, upsertVoiceTranscript]);

    // -----------------------------------------
    // CONNECT WEBSOCKET
    // -----------------------------------------
    const connectWebSocket = useCallback(() => {
        // Close existing connection if any
        if (wsRef.current) {
            try {
                wsRef.current.close();
            } catch (e) {
                // Ignore close errors
            }
        }

        setConnectionError(null);

        try {
            wsRef.current = new WebSocket(wsUrl);
        } catch (error) {
            console.error('[WS] Connection failed:', error);
            setConnectionError('Failed to connect to server');
            return;
        }

        wsRef.current.onopen = () => {
            console.log('[WS] Connected');
            setIsConnected(true);
            setConnectionError(null);

            setTimeout(() => {
                if (isWebSocketOpen()) {
                    sendChatStartEvent();
                }
            }, WS_SEND_DELAY);
        };

        wsRef.current.onmessage = handleWebSocketMessage;

        wsRef.current.onclose = () => {
            console.log('[WS] Disconnected');
            setIsConnected(false);
        };

        wsRef.current.onerror = (err) => {
            console.error('[WS] Error:', err);
            setConnectionError('Connection error. Attempting to reconnect...');
        };
    }, [wsUrl, isWebSocketOpen, sendChatStartEvent, handleWebSocketMessage]);

    // -----------------------------------------
    // CLOSE CHAT
    // -----------------------------------------
    const closeChat = useCallback(async () => {
        console.log('[Chat] Closing');

        if (isVoiceChatActiveRef.current) {
            safeSend({ event: 'chat_end' });
        }

        await stopAgentPlayback();
        await cleanupVoiceResources();

        // Close WebSocket
        if (wsRef.current) {
            try {
                wsRef.current.close();
            } catch (e) {
                // Ignore
            }
            wsRef.current = null;
        }

        // Close audio context
        if (audioContextRef.current) {
            try {
                audioContextRef.current.close();
            } catch (e) {
                // Ignore
            }
            audioContextRef.current = null;
        }

        if (playbackAudioContextRef.current) {
            try {
                playbackAudioContextRef.current.close();
            } catch (e) {
                // Ignore
            }
            playbackAudioContextRef.current = null;
            playbackTimeRef.current = 0;
        }

        setIsConnected(false);
        setIsMinimized(true);
    }, [cleanupVoiceResources, safeSend, stopAgentPlayback]);

    // -----------------------------------------
    // OPEN CHAT
    // -----------------------------------------
    const openChat = useCallback(() => {
        setIsMinimized(false);

        if (!isWebSocketOpen()) {
            connectWebSocket();
        }

        // Scroll to bottom after render
        setTimeout(() => {
            if (messagesContainerRef.current) {
                messagesContainerRef.current.scrollTop =
                    messagesContainerRef.current.scrollHeight;
            }
            scrollToBottom();
        }, 100);
    }, [isWebSocketOpen, connectWebSocket, scrollToBottom]);

    // -----------------------------------------
    // INPUT HANDLERS
    // -----------------------------------------
    const handleInputChange = useCallback((e) => {
        setInput(e.target.value);
    }, []);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            sendMessage(input);
        }
    }, [input, sendMessage]);

    const handleSendClick = useCallback(() => {
        sendMessage(input);
    }, [input, sendMessage]);

    // -----------------------------------------
    // LIFECYCLE — CONNECT ON MOUNT
    // -----------------------------------------
    useEffect(() => {
        connectWebSocket();

        return () => {
            // Cleanup on unmount
            if (wsRef.current) {
                try {
                    wsRef.current.close();
                } catch (e) {
                    // Ignore
                }
            }
            if (processorRef.current) {
                try {
                    processorRef.current.disconnect();
                } catch (e) {
                    // Ignore
                }
                processorRef.current = null;
            }
            if (mediaStreamSourceRef.current) {
                try {
                    mediaStreamSourceRef.current.disconnect();
                } catch (e) {
                    // Ignore
                }
                mediaStreamSourceRef.current = null;
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }
            if (isRecordingRef.current && mediaRecorderRef.current) {
                try {
                    mediaRecorderRef.current.stop();
                } catch (e) {
                    // Ignore
                }
            }
            if (audioContextRef.current) {
                try {
                    audioContextRef.current.close();
                } catch (e) {
                    // Ignore
                }
            }
            if (playbackAudioContextRef.current) {
                try {
                    playbackAudioContextRef.current.close();
                } catch (e) {
                    // Ignore
                }
            }
            if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
            }
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentId, userId, wsUrl]);

    // =========================================
    // STYLE VARIABLES
    // =========================================
    // CSS custom properties resolve inside inline style props, so this stays in
    // step with the brand token; the literal is the no-stylesheet fallback.
    const defaultAccentBg = 'var(--wr-gradient-brand, linear-gradient(92deg, #001FCC 0%, #0026FF 48%, #3355FF 100%))';

    const finalHeaderBg = headerColor || backgroundColor || defaultAccentBg;
    const finalButtonBg = buttonColor || defaultAccentBg;
    const finalUserMessageBg = headerColor || defaultAccentBg;
    const finalHeaderTextColor = textColor || 'white';
    const finalChatTextColor = chatTextColor || textColor || '#374151';

    const inlineStyles = {
        '--widget-bg': backgroundColor || 'white',
        '--widget-header-bg': finalHeaderBg,
        '--widget-header-text': finalHeaderTextColor,
        '--widget-chat-text': finalChatTextColor,
        '--widget-user-msg-bg': finalUserMessageBg,
        '--widget-user-msg-text': finalChatTextColor,
        '--widget-bot-msg-bg': 'white',
        '--widget-bot-msg-text': finalChatTextColor,
        '--widget-input-bg': backgroundColor || 'white',
        '--widget-input-border': '#e5e7eb',
        '--widget-input-text': finalChatTextColor,
        '--widget-send-btn-bg': finalButtonBg,
        '--widget-button-text': finalHeaderTextColor,
        '--widget-border-radius': borderRadius || '24px'
    };

    // =========================================
    // RENDER — MINIMIZED STATE
    // =========================================
    if (isMinimized) {
        return (
            <div className="chat-widget-minimized" onClick={openChat} style={inlineStyles}>
                <div className="minimized-content">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                            d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
                            fill="currentColor"
                        />
                    </svg>
                    <span>Need Help?</span>
                </div>
            </div>
        );
    }

    // =========================================
    // RENDER — FULL CHAT
    // =========================================
    return (
        <div className="chat-widget" style={inlineStyles}>
            {/* ---- Header ---- */}
            <div className="chat-header">
                <div className="header-content">
                    <div className="header-info">
                        <div className="avatar">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                <path d="M12 8V12L14 14" stroke="currentColor" strokeWidth="2" />
                            </svg>
                        </div>
                        <div>
                            <h3>AI Assistant</h3>
                            <div className="status">
                                <span
                                    className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`}
                                />
                                <span>{isConnected ? 'Online' : 'Connecting...'}</span>
                            </div>
                        </div>
                    </div>
                    <div className="header-actions">
                        {!isConnected && connectionError && (
                            <div className="error-tooltip" title={connectionError}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    />
                                </svg>
                            </div>
                        )}
                        <button className="minimize-btn" onClick={closeChat}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M19 13H5V11H19V13Z" fill="currentColor" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* ---- Messages ---- */}
            <div className="messages-container" ref={messagesContainerRef}>
                {messages.length === 0 && (
                    <div className="welcome-message">
                        <div className="welcome-icon">💬</div>
                        <h4>Welcome to AI Assistant</h4>
                        <p>Type a message or click the mic to talk</p>
                        <small>Click mic to start live voice chat</small>
                    </div>
                )}

                {messages.map((msg, idx) => (
                    <div key={idx} className={`message ${msg.sender}`}>
                        <div className="message-avatar">
                            {msg.sender === 'bot'
                                ? '🤖'
                                : msg.sender === 'user'
                                    ? '👤'
                                    : 'ℹ️'}
                        </div>
                        <div className="message-content">
                            <div className="message-text">{msg.text}</div>
                            <div className="message-time">{msg.timestamp}</div>
                        </div>
                    </div>
                ))}

                {typing && (
                    <div className="message bot">
                        <div className="message-avatar">🤖</div>
                        <div className="message-content">
                            <div className="typing-indicator">
                                <span />
                                <span />
                                <span />
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* ---- Input Area ---- */}
            <div className="input-area">
                {isRecording && (
                    <div className="recording-overlay">
                        <div className="recording-panel">
                            <div className="recording-info">
                                <div className="recording-duration">
                                    <span className="recording-dot" />
                                    {formatDuration(recordingDuration)}
                                </div>
                                <div className="recording-instruction">
                                    Voice chat live
                                </div>
                            </div>
                            <div className="recording-waveform">
                                <div className="waveform-bars">
                                    {[...Array(AMPLITUDE_BARS_COUNT)].map((_, i) => (
                                        <div
                                            key={i}
                                            className="waveform-bar"
                                            style={{
                                                height: `${Math.max(
                                                    AMPLITUDE_MIN_HEIGHT,
                                                    Math.min(
                                                        AMPLITUDE_MAX_HEIGHT,
                                                        recordingAmplitude *
                                                        AMPLITUDE_SCALE *
                                                        (0.5 + Math.sin((i * Math.PI) / 10) * 0.5)
                                                    )
                                                )}px`,
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div className="input-container">
                    <input
                        type="text"
                        value={input}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Type a message..."
                        className="message-input"
                        disabled={!isConnected || isVoiceChatActive}
                    />
                    {/* <div className="input-actions">
                        {input.trim() ? (
                            <button
                                className="send-btn"
                                onClick={handleSendClick}
                                disabled={!isConnected}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M22 2L11 13"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        d="M22 2L15 22L11 13L2 9L22 2Z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        ) : (
                            <button
                                className={`voice-btn ${isRecording ? 'recording-active' : ''}`}
                                onClick={toggleRecording}
                                disabled={!isConnected}
                                title={isRecording ? 'Stop recording and send' : 'Start recording'}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M12 15C13.66 15 15 13.66 15 12V6C15 4.34 13.66 3 12 3C10.34 3 9 4.34 9 6V12C9 13.66 10.34 15 12 15Z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill={isRecording ? '#ef4444' : 'none'}
                                    />
                                    <path
                                        d="M19 12C19 15.86 15.86 19 12 19C8.14 19 5 15.86 5 12"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    />
                                    <path d="M12 22V19" stroke="currentColor" strokeWidth="2" />
                                </svg>
                            </button>
                        )}
                    </div> */}
                    <div className="input-actions">
                        <button
                            className={`voice-btn ${isVoiceChatActive ? 'recording-active' : ''}`}
                            onClick={toggleVoiceChat}
                            disabled={!isConnected}
                            title={isVoiceChatActive ? 'End voice chat' : 'Start voice chat'}
                        >
                            {isVoiceChatActive ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M6 6H18V18H6V6Z"
                                        fill="currentColor"
                                    />
                                </svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M12 15C13.66 15 15 13.66 15 12V6C15 4.34 13.66 3 12 3C10.34 3 9 4.34 9 6V12C9 13.66 10.34 15 12 15Z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    />
                                    <path
                                        d="M19 12C19 15.86 15.86 19 12 19C8.14 19 5 15.86 5 12"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    />
                                    <path d="M12 22V19" stroke="currentColor" strokeWidth="2" />
                                </svg>
                            )}
                        </button>
                        <button
                            className="send-btn"
                            onClick={handleSendClick}
                            disabled={!input.trim() || !isConnected || isVoiceChatActive}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M22 2L11 13"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M22 2L15 22L11 13L2 9L22 2Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </div>

                </div>
                {!isConnected && connectionError && (
                    <div className="connection-error">{connectionError}</div>
                )}
            </div>
        </div>
    );
}
